import { FOOTER_ROW_STYLES, FILTER_KEYS } from '../consts';
import useFilters from '../hooks/useFilters';
import { useQuery } from '@tanstack/react-query';
import { getDgcOrders } from 'api/requests/dgc/dgcOrders';
import { DGC_ORDERS_PAGE_SIZE } from 'shared/consts/dgcOrders';
import useFilterSelectors from 'shared/hooks/useFilterSelectors';
import { queryKeyBuilder } from 'shared/query';

function OrdersFooter() {
  const urlQueryParams = useFilters();
  const { onSingleFilterSelected } = useFilterSelectors();

  const { data: ordersData, isPending } = useQuery({
    queryKey: queryKeyBuilder.dgcOrdersToShow(urlQueryParams),
    queryFn: () => getDgcOrders(urlQueryParams),
  });

  const currentPage = Number(urlQueryParams.pageNum);
  const totalOrders = ordersData?.total ?? 0;
  const totalPages = Math.ceil(totalOrders / DGC_ORDERS_PAGE_SIZE);

  const getPageRange = () => {
    const start = Math.max(currentPage - 2, 1);
    const end = Math.min(currentPage + 2, totalPages);
    const pagesToShow = [];
    const separator = ['.', '.', '.'];

    if (start > 1) {
      pagesToShow.push(1);
    }
    if (start > 2) {
      pagesToShow.push(...separator);
    }
    for (let i = start; i <= end; i++) {
      pagesToShow.push(i);
    }
    if (end < totalPages && end !== start - 1) {
      pagesToShow.push(...separator);
    }
    if (end < totalPages) {
      pagesToShow.push(totalPages);
    }
    return pagesToShow;
  };

  const rangeStart = totalOrders === 0 ? 0 : (currentPage - 1) * DGC_ORDERS_PAGE_SIZE + 1;
  const rangeEnd = Math.min(currentPage * DGC_ORDERS_PAGE_SIZE, totalOrders);
  const displayRangeLabel = `${rangeStart} - ${rangeEnd}`;

  return isPending ? null : (
    <div className={FOOTER_ROW_STYLES}>
      <span className="font-inter font-normal text-sm text-palette-secondary-grey">
        Showing {displayRangeLabel} of {totalOrders} results
      </span>
      <div className="pagination flex justify-between text-sm gap-x-12">
        <button
          onClick={() => onSingleFilterSelected(FILTER_KEYS.pageNum, String(currentPage - 1))}
          disabled={currentPage === 1}
          className="text-palette-secondary uppercase before:content-['\<'] disabled:text-palette-light-grey"
        >
          <span className="underline">Previous</span>
        </button>
        <div className="flex justify-between gap-x-1.5">
          {getPageRange().map((page, index) => {
            if (typeof page === 'number') {
              return (
                <button
                  key={index}
                  onClick={() => onSingleFilterSelected(FILTER_KEYS.pageNum, String(page))}
                  disabled={page === currentPage}
                  className={
                    page === currentPage
                      ? 'text-palette-burgundy bg-palette-burgundy/10 rounded-full py-1 px-3'
                      : 'hover:text-palette-burgundy hover:bg-palette-burgundy/10 rounded-full py-1 px-3'
                  }
                >
                  {page}
                </button>
              );
            }
            return (
              <span className="py-1 px-1" key={index}>
                {page}
              </span>
            );
          })}
        </div>
        <button
          onClick={() => onSingleFilterSelected(FILTER_KEYS.pageNum, String(currentPage + 1))}
          disabled={currentPage >= totalPages}
          className="text-palette-secondary uppercase after:content-['\>'] disabled:text-palette-light-grey"
        >
          <span className="underline">Next</span>
        </button>
      </div>
    </div>
  );
}

export default OrdersFooter;
